import { useEffect, useRef, useState } from 'react';
import * as tf from '@tensorflow/tfjs-core';
import '@tensorflow/tfjs-backend-webgl';
import * as tfjsWasm from '@tensorflow/tfjs-backend-wasm';
tfjsWasm.setWasmPaths(
  `https://cdn.jsdelivr.net/npm/@tensorflow/tfjs-backend-wasm@${tfjsWasm.version_wasm}/dist/`
);
import * as bodySegmentation from '@tensorflow-models/body-segmentation';

import Container from '@mui/material/Container';

import Dialog from 'components/ui/Dialog';
import Placeholder from 'components/Placeholder';
import RemoveVideo from 'components/RemoveVideo';
import UploadVideo from 'components/UploadVideo';

import { ReactComponent as WideoLogo } from 'assets/gray-logo.svg';
import BrowserNotSupportedIcon from '@mui/icons-material/BrowserNotSupported';

import { setupModel } from 'utils/setupModel';
import { isSafari, setBackendAndEnvFlags } from 'utils/Util';

import useCamera from 'hooks/useCamera';

import { DIALOG_ACTIONS, STATE } from 'constants/index';

import { TAny } from 'types';

let segmenter: TAny;

const BGRemover = () => {
  const [loading, setLoading] = useState(false);
  const [isPanelVisible, setIsPanelVisible] = useState(false);
  const [isDisabledButton, setIsDisabledButton] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [result, setResult] = useState(['backend not loaded', false]);
  const videoRef = useRef<TAny>(null);
  const outputVideoRef = useRef<TAny>(null);
  const sourceRef = useRef<TAny>(null);
  const canvasRef = useRef<TAny>(null);
  const downloadRef = useRef<TAny>(null);

  const refsItems = [
    videoRef,
    sourceRef,
    canvasRef,
    downloadRef,
    outputVideoRef,
  ];

  useEffect(() => {
    if (!result[1]) {
      loadBackend()
        .then((backend) =>
          setResult([`Successfully loaded ${backend} backend`, true])
        )
        .catch((e) => setResult([`Failed to load backend: ${e}`, true]));
    }

    const callback = async () => {
      await initialSetUp();
      setLoading(false);
    };
    callback();
  }, []);

  useEffect(() => {
    addCanvasWebGLContextLossEventListener();

    return () => {
      removeCanvasWebGLContextLossEventListener();
    };
  }, []);

  const createSegmenter = async () => {
    const model = bodySegmentation.SupportedModels.BodyPix;
    const segmenterConfig: TAny = {
      architecture: 'MobileNetV1',
      outputStride: 16,
      multiplier: 0.75,
      quantBytes: 4,
    };

    return bodySegmentation.createSegmenter(model, segmenterConfig);
  };

  const updateVideo = async (file: string) => {
    handleOpen();
    URL.revokeObjectURL(videoRef.current.currentSrc);

    if (file) {
      sourceRef.current.src = file;
    }

    videoRef.current.load();
    await new Promise((resolve) => {
      videoRef.current.onloadeddata = () => {
        resolve(videoRef.current);
      };
    });

    videoRef.current.width = videoRef.current.videoWidth;
    videoRef.current.height = videoRef.current.videoHeight;
    canvasRef.current.width = videoRef.current.videoWidth;
    canvasRef.current.height = videoRef.current.videoHeight;

    await run();

    setLoading(false);
  };

  const loadBackend = async () => {
    await tf.ready();
    return tf.getBackend();
  };

  const renderResult = async () => {
    const { drawToCanvas, drawFromVideo } = useCamera(
      videoRef,
      canvasRef,
      downloadRef,
      outputVideoRef
    );
    let segmentation = null;

    if (segmenter !== null) {
      try {
        if (segmenter.segmentPeople !== null) {
          segmentation = await segmenter.segmentPeople(videoRef.current, {
            flipHorizontal: false,
            multiSegmentation: false,
            segmentBodyParts: true,
            segmentationThreshold: STATE.visualization.foregroundThreshold,
          });
        }
      } catch (error) {
        segmenter.dispose();
        segmenter = null;
        alert(error);
      }

      const [backend] = STATE.backend.split('-');
      if (backend === 'tfjs') {
        for (const value of segmentation) {
          const mask = value.mask;
          const tensor = await mask.toTensor();
          const res = tensor.dataToGPU();
          const webGLBackend: TAny = tf.backend();
          const buffer = webGLBackend.gpgpu.createBufferFromTexture(
            res.texture,
            1,
            1
          );
          webGLBackend.gpgpu.downloadFloat32MatrixFromBuffer(buffer, 1);
          res.tensorRef.dispose();
        }
      }
    }

    if (segmentation && segmentation.length > 0 && !STATE.isModelChanged) {
      const vis = STATE.modelConfig.visualization;
      const options = STATE.visualization;

      if (vis === 'binaryMask') {
        const data = await bodySegmentation.toBinaryMask(
          segmentation,
          { r: 0, g: 0, b: 0, a: 0 },
          { r: 0, g: 255, b: 0, a: 255 },
          false,
          options.foregroundThreshold
        );
        await bodySegmentation.drawMask(
          canvasRef.current,
          videoRef.current,
          data,
          options.maskOpacity,
          options.maskBlur
        );
      } else {
        drawFromVideo(canvasRef.current.getContext('2d'));
      }
    }
    drawToCanvas(canvasRef.current);
  };

  const runFrame = async (clearCtx: () => void, stop: () => void) => {
    if (videoRef.current.paused) {
      stop();
      setIsDisabledButton(false);
      return;
    }
    setIsDisabledButton(true);
    await renderResult();
    requestAnimationFrame(() => runFrame(clearCtx, stop));
  };

  const run = async () => {
    const { clearCtx, start, stop } = useCamera(
      videoRef,
      canvasRef,
      downloadRef,
      outputVideoRef
    );
    const [runtime] = STATE.backend.split('-');
    if (runtime === 'tfjs') {
      const warmUpTensor = tf.fill(
        [videoRef.current.height, videoRef.current.width, 3],
        0,
        'float32'
      );
      if (segmenter.segmentPeople !== null) {
        await segmenter.segmentPeople(warmUpTensor, {
          flipHorizontal: false,
          multiSegmentation: false,
          segmentBodyParts: true,
          segmentationThreshold: STATE.visualization.foregroundThreshold,
        });
      }
      warmUpTensor.dispose();
    }
    videoRef.current.pause();
    videoRef.current.currentTime = 0;
    videoRef.current.play();
    start();
    await new Promise((resolve) => {
      videoRef.current.onseeked = () => {
        resolve(videoRef.current);
      };
    });
    await runFrame(clearCtx, stop);
  };

  const handleOpen = () => {
    setIsPanelVisible(true);
    setLoading(true);
  };

  const initialSetUp = async () => {
    setupModel(STATE.model);

    await setBackendAndEnvFlags(STATE.flags, STATE.backend);

    try {
      segmenter = await createSegmenter();
    } catch (error) {
      segmenter = null;
      alert(error);
    }
  };

  const onChangeVideo = async (file: string) => {
    if (file === '') {
      setShowDialog(true);
      return;
    }
    await updateVideo(file);
  };

  const onCloseDialog = () => {
    setShowDialog(false);
  };

  const addCanvasWebGLContextLossEventListener = () => {
    if (canvasRef.current) {
      canvasRef.current.addEventListener('webglcontextlost', () => {
        window.location.reload();
      });
    }
  };

  const removeCanvasWebGLContextLossEventListener = () => {
    if (canvasRef.current) {
      canvasRef.current.removeEventListener('webglcontextlost', () => {
        //
      });
    }
  };

  return (
    <main>
      <Container maxWidth={false}>
        {isSafari() ? (
          <Placeholder
            logo={<WideoLogo />}
            icon={<BrowserNotSupportedIcon />}
            title="Your browser is not supported by this app"
            text="We recommend Chrome to use this app."
            innerStyles={{ mx: 10 }}
          />
        ) : (
          <>
            <UploadVideo
              onChange={onChangeVideo}
              isDisabledButton={isDisabledButton}
            />
            <RemoveVideo
              loading={loading}
              ref={refsItems as TAny}
              isPanelVisible={isPanelVisible}
              isDisabledButton={isDisabledButton}
            />
          </>
        )}
      </Container>
      <Dialog
        open={showDialog}
        description="Your file is too size. Please try a smaller size file"
        actions={DIALOG_ACTIONS}
        onClose={onCloseDialog}
      />
    </main>
  );
};

export default BGRemover;
